import { PropsWithChildren, StrictMode, useMemo, useRef } from 'react';
import { I18nextProvider } from 'react-i18next';

import { LoggerProvider } from '../../contexts/logger-context';
import { PlayerRefProvider } from '../../contexts/player-ref-context';
import { getI18nInstance } from '../../i18n/get-i18n-instance';
import { PlayerConfig, SettingsConfig } from '../player';
import { usePlayerColorsStyles } from '../user-interfaces/hooks/use-player-colors-styles';

type OptionsProcessorProps = PropsWithChildren<{
  options: Options;
}>;

type Options = Pick<PlayerConfig, 'lng' | 'debug'> & {
  settings?: Pick<
    SettingsConfig,
    'theme' | 'primaryColor' | 'controlBarColor' | 'iconColor' | 'bigPlayButtonIconColor'
  >;
};

export function OptionsProcessor({ options, children }: OptionsProcessorProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const i18nInstance = useMemo(() => getI18nInstance(options.lng), [options.lng]);

  const playerColors = usePlayerColorsStyles({
    theme: options.settings?.theme,
    customPrimaryColor: options.settings?.primaryColor,
    controlBarColor: options.settings?.controlBarColor,
    iconColor: options.settings?.iconColor,
    bigPlayButtonIconColor: options.settings?.bigPlayButtonIconColor,
  });

  return (
    <StrictMode>
      <I18nextProvider i18n={i18nInstance}>
        <LoggerProvider debug={options.debug}>
          <PlayerRefProvider playerRef={wrapperRef}>
            <div
              ref={wrapperRef}
              style={{ ...playerColors, display: 'contents' }}
              className={options.settings?.theme?.toString() || 'dark'}
            >
              {children}
            </div>
          </PlayerRefProvider>
        </LoggerProvider>
      </I18nextProvider>
    </StrictMode>
  );
}
